import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  type Client,
  CommunicationSource,
  CommunicationStatus,
} from "@/briefpoint-client";
import Button from "@/components/Button";
import ConfirmCodeResetModal from "@/components/Modals/ConfirmCodeReset";
import LinkAndCodeModal from "@/components/Modals/LinkandCodeModal";
import StartOverWarningModal from "@/components/Modals/StartOverWarning";
import { toast } from "@/components/ui/toast";
import { useCommunicationApi, useCredentialApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import {
  getCredentialOptions,
  getFirmCommunicationsOptions,
} from "@/lib/query-options";
import { queryClient } from "@/lib/queryClient";
import type { ViewCommunicationResponse } from "@/lib/types";
import styles from "./DocumentListTableRowReview.module.scss";

type Props = {
  communication?: ViewCommunicationResponse;
  createReview: () => void;
  client?: Client;
  closeReview: () => void;
};

const viewed = [
  CommunicationStatus.Open,
  CommunicationStatus.InProgress,
  CommunicationStatus.Complete,
  CommunicationStatus.ChangesRequested,
  CommunicationStatus.Closed,
];

export function DocumentListTableRowReview(props: Props) {
  const [showStartOverModal, setShowStartOverModal] = useState(false);
  const { user } = useAuth();

  const clientResponseNumber =
    props.communication?.externalCommunicationItems?.filter(
      (ci) => ci.source === CommunicationSource.Client,
    )?.length ?? 0;

  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showCodeChangeModal, setShowCodeChangeModal] = useState(false);
  const [showNewCodeSuccess, setShowNewCodeSuccess] = useState(false);

  const credentialApi = useCredentialApi();
  const communicationApi = useCommunicationApi();

  const credentialQuery = useQuery({
    ...getCredentialOptions(
      credentialApi,
      props.client?.id ?? props.communication?.sharedWithObjectId ?? "",
    ),
    enabled: !!(props.client?.id ?? props.communication?.sharedWithObjectId),
  });

  const communicationMutation = useMutation({
    mutationFn: async (communication: ViewCommunicationResponse) => {
      await communicationApi.communicationUpdateCommunication({
        id: communication.id,
        putClientCommunication: { ...communication },
      });
    },
    onSuccess: () => {
      toast.success("Communication updated successfully.");
    },
    onError: () => {
      toast.error("Failed to update communication.");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        ...getFirmCommunicationsOptions(communicationApi, user?.firmId ?? ""),
      });
      props.closeReview();
    },
  });

  const removeClientAccess = useMutation({
    mutationFn: () => {
      return communicationApi.communicationUpdateCommunication({
        id: props.communication?.id ?? "",
        putClientCommunication: {
          ...(props.communication ?? {}),
          sharedWithObjectType: undefined,
          sharedWithObjectId: undefined,
          status: CommunicationStatus.Closed,
        },
      });
    },
    onSuccess: () => {
      toast.success("Client access removed successfully.");
    },
    onError: () => {
      toast.error("Failed to remove client access.");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        ...getFirmCommunicationsOptions(communicationApi, user?.firmId ?? ""),
      });
    },
  });

  const createNewCredentialMutation = useMutation({
    mutationFn: async (clientId: string) =>
      credentialApi.credentialUpdate({ clientId }),
    onSuccess: () => {
      toast.success("New credential created successfully.");
      credentialQuery.refetch();
      setShowNewCodeSuccess(true);
    },
    onError: () => {
      toast.error("Failed to create new credential. Please try again.");
    },
  });

  function handleLinkAndCodeClose(showChangeCode: boolean) {
    setShowLinkModal(false);
    if (showChangeCode) {
      setShowCodeChangeModal(true);
    }
  }
  function handleCodeChangeClose(goBack: boolean) {
    setShowCodeChangeModal(false);
    if (goBack) {
      setShowLinkModal(true);
    }
  }

  function handleConfirmCodeChange() {
    const clientId =
      props.client?.id ?? props.communication?.sharedWithObjectId;

    if (!clientId) {
      return;
    }
    setShowCodeChangeModal(false);
    setShowLinkModal(true);
    createNewCredentialMutation.mutate(clientId);
  }

  return (
    <div>
      <div className={styles.clientActivityRowContainer}>
        <div className={styles.clientActivityHeader}>
          Client Activity Details
          <hr className={styles.clientActivityHr} />
        </div>
        <div className={styles.clientActivityContent}>
          <table className={styles.clientActivityTable}>
            <thead>
              <tr>
                <th className="client-activity-th">Number of Questions</th>
                <th className="client-activity-th">Viewed By Client?</th>
                <th className="client-activity-th">Client Answered</th>
              </tr>
              <tr className={styles.clientActivityTr}>
                <td style={{ paddingTop: "0px" }}>
                  {
                    props.communication?.communicationInfo?.requestedReviewIds
                      ?.length
                  }
                </td>
                <td style={{ paddingTop: "0px" }}>
                  {props.communication?.status &&
                  viewed.includes(props.communication.status)
                    ? "Yes"
                    : "No"}
                </td>
                <td style={{ paddingTop: "0px" }}>{clientResponseNumber}</td>
              </tr>
            </thead>
          </table>
          <Button
            className={styles.removeClientAccessBtn}
            variant="outline"
            onClick={() => {
              if (clientResponseNumber !== 0) {
                setShowStartOverModal(true);
              } else {
                if (props.communication) {
                  removeClientAccess.mutate();
                }
              }
            }}
          >
            {props.communication?.status === CommunicationStatus.Pending
              ? "Delete"
              : "Start Over"}
          </Button>
          {props.communication?.status === CommunicationStatus.Complete && (
            <Button
              className={styles.removeClientAccessBtn}
              variant="outline"
              onClick={() => {
                communicationMutation.mutate({
                  ...(props.communication ?? { id: "" }),
                  status: CommunicationStatus.Open,
                });
              }}
            >
              Re-open
            </Button>
          )}
          <StartOverWarningModal
            isShow={showStartOverModal}
            onClose={(success: boolean) => {
              if (success) {
                if (props.communication) {
                  removeClientAccess.mutate();
                }
                props.closeReview();
              }
              setShowStartOverModal(false);
            }}
          />
          {props.communication?.status === CommunicationStatus.Pending ? (
            <Button
              className={styles.viewLinkCodeBtn}
              variant="default"
              onClick={() => {
                if (props.createReview) {
                  props.createReview();
                }
              }}
            >
              Continue
            </Button>
          ) : (
            <Button
              className={styles.viewLinkCodeBtn}
              variant="default"
              onClick={() => {
                setShowLinkModal(true);
              }}
            >
              View Link and Code
            </Button>
          )}
          <LinkAndCodeModal
            isShow={showLinkModal}
            onClose={handleLinkAndCodeClose}
            credential={credentialQuery.data}
            showNewCodeSuccess={showNewCodeSuccess}
            client={props.client}
          />
          <ConfirmCodeResetModal
            isShow={showCodeChangeModal}
            onClose={handleCodeChangeClose}
            onConfirm={handleConfirmCodeChange}
          />
        </div>
      </div>
    </div>
  );
}
